// "우리가 고칠 수 있는 에러인가" 를 스택 출처·네트워크 상태·브라우저 고정 문구로 판정해 origin 을
// 확정한다. 메시지 패턴 목록이 아니라 속성(스택 출처)으로 판정하는 것이 노션 표준의 요구다.

import type { AppError, ErrorOrigin } from './AppError'
import {
  CROSS_ORIGIN_MASKED_MESSAGE,
  ENVIRONMENT_MESSAGES,
  EXTENSION_STACK_MARKERS,
  type ReportingPolicy,
} from './policy'

// 원본(cause)이 있으면 그 스택이 발생 지점이다. 봉투 자신의 스택은 감싼 자리일 뿐이다.
// DOMException 은 환경에 따라 Error 의 instanceof 체인에 없으므로 모양(객체)으로 본다.
export const stackOf = (error: AppError): string | undefined => {
  const cause = error.cause
  if (cause && typeof cause === 'object') return (cause as { stack?: string }).stack
  return error.stack
}

// 포트(localhost:5173)와 줄·열 번호(:12:5)가 섞여 있어 ':' 에서 끊지 않고 origin 경계로 비교한다.
const FILE_URL = /https?:\/\/[^\s)]+/g

const isFromOrigin = (file: string, origin: string) => {
  const base = origin.endsWith('/') ? origin : `${origin}/`
  return file === origin || file.startsWith(base)
}

const isOutsideBundle = (stack: string, bundleOrigins: string[]) => {
  const files = stack.match(FILE_URL) ?? []
  if (files.length === 0) return false
  return files.every((file) => !bundleOrigins.some((origin) => isFromOrigin(file, origin)))
}

export const classify = (error: AppError, policy: ReportingPolicy): ErrorOrigin => {
  const stack = stackOf(error)

  if (stack) {
    if (EXTENSION_STACK_MARKERS.some((marker) => marker.test(stack))) return 'external'
    if (isOutsideBundle(stack, policy.bundleOrigins)) return 'external'
  }

  // 오프라인·연결 불안정은 네트워크 실패의 원인일 뿐이다. 그때 난 코드 버그(client)까지 environment 로 묻지 않는다.
  if (error.kind === 'network' && (policy.isOffline() || policy.isNetworkUnsettled())) {
    return 'environment'
  }
  if (ENVIRONMENT_MESSAGES.some((pattern) => pattern.test(error.message))) return 'environment'

  if (error.kind === 'client' && !stack) {
    // 스택이 없어도 교차 출처 스크립트가 가려진 것은 출처를 아는 셈이다 — 우리 번들은 같은 출처라 이 문구로 오지 않는다
    return CROSS_ORIGIN_MASKED_MESSAGE.test(error.message) ? 'external' : 'unknown'
  }

  return error.origin
}
